import React, { useState, useRef } from "react";
import { useStore } from "../../../stores";
import { Modal } from "../../../components";
import html2canvas from "html2canvas";
import { BsDownload } from "react-icons/bs";
import clsx from "clsx";
import { FlowResults } from "../../../models/flowResults";
import { FlowResultsContent } from "./FlowResultsContent";

interface WaFlowProps {
  payload: {
    flow_token: string;
    flow_id: string;
    message: string;
    flow_name: string;
    button_label: string;
    response?: {
      fieldsMap: Record<string, any>;
      completed: boolean;
    } & Record<string, any>; // dynamic flow response
  };
}

const WaFlowSent: React.FC<WaFlowProps> = ({ payload }) => {
  const [loading, setLoading] = useState(false);
  const { getFlowResults, getFlowResultAttachment, selectedChat } =
    useStore("chats");
  const isDynamicFlow = !!payload.response;
  const [flowResult, setFlowResult] = useState<FlowResults>();
  const [resultsOpen, setResultsOpen] = useState(false);
  const [resultsError, setResultsError] = useState(false);

  const [downloading, setDownloading] = useState(false);
  const resultsRef = useRef<HTMLDivElement>(null);

  const fetchFlowResults = async () => {
    setResultsOpen(true);
    setResultsError(false);
    if (isDynamicFlow) {
      setFlowResult({
        result: {},
        screen: [],
      });
      return;
    }
    try {
      setLoading(true);
      const data = await getFlowResults(payload.flow_token, payload.flow_id);
      if (!data) throw new Error("Flow results unavailable");
      setFlowResult(data);
    } catch (error) {
      setResultsError(true);
    } finally {
      setLoading(false);
    }
  };

  const downloadAsImage = async () => {
    if (!resultsRef.current) return;

    try {
      setDownloading(true);

      // Create a clone of the results div to avoid modifying the original
      const clonedResults = resultsRef.current.cloneNode(true) as HTMLElement;

      // Apply some styling for better image output
      clonedResults.style.padding = "20px";
      clonedResults.style.backgroundColor = "white";
      clonedResults.style.borderRadius = "8px";
      clonedResults.style.width = "30%";

      // Temporarily append to body but hide it
      clonedResults.style.position = "absolute";
      clonedResults.style.left = "-9999px";
      document.body.appendChild(clonedResults);

      // Generate canvas from the element
      const canvas = await html2canvas(clonedResults, {
        backgroundColor: "#ffffff",
        scale: 2, // Higher quality
        logging: false,
        useCORS: true,
      });

      // Remove the cloned element
      document.body.removeChild(clonedResults);

      // Create download link
      const link = document.createElement("a");
      link.download = `Chat-${selectedChat?.id} Flow-Result-${
        payload.flow_name || "flow"
      }.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (error) {
      console.error("Error generating image:", error);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div>
      <h2 className="h-6 has-text-weight-medium has-text-white mb-1 subtitle">
        Flow Name: {payload.flow_name ?? "Custom flow"}
      </h2>
      <p>{payload.message}</p>
      <div className="is-flex is-justify-content-center my-2">
        <button
          className="button is-primary is-success is-small"
          onClick={fetchFlowResults}
          disabled={loading}
        >
          {payload.button_label}
        </button>
      </div>
      <small>By clicking this button, you can view the flow results</small>

      {resultsOpen && (
        <Modal
          title={() => (
            <h2 className="is-flex title is-5 is-align-items-center">
              Flow Results
              <button
                className={clsx("button is-small is-ghost has-text-black", {
                  "is-loading": downloading,
                })}
                onClick={downloadAsImage}
                disabled={downloading || loading || resultsError || !flowResult}
              >
                <BsDownload size={14} />
              </button>
            </h2>
          )}
          isOpen
          isLargeScreen={!!flowResult?.display}
          onClose={() => {
            setResultsOpen(false);
            setFlowResult(undefined);
          }}
        >
          <div className="content" ref={resultsRef}>
            {loading ? (
              <p role="status">Loading Flow results…</p>
            ) : resultsError ? (
              <div role="alert">
                <p>Could not load Flow results. Please try again.</p>
                <button
                  type="button"
                  className="button is-small"
                  onClick={fetchFlowResults}
                >
                  Retry
                </button>
              </div>
            ) : (
              <FlowResultsContent
                data={flowResult}
                response={payload.response}
                loadAttachment={(attachment) =>
                  getFlowResultAttachment(payload.flow_token, attachment)
                }
              />
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default WaFlowSent;
